// ============================================
// FORGOT PASSWORD PAGE + i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { useState } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/forgot-password' as any)({
  component: ForgotPasswordPage,
});

const forgotPasswordSchema = z.object({
  email: z.string().email('Geçerli bir e-posta adresi giriniz'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [isSuccess, setIsSuccess] = useState(false);

  const forgotPasswordMutation = useMutation({
    mutationFn: (data: ForgotPasswordFormData) => authApi.forgotPassword(data),
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-maritime-600 to-maritime-800 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.company')}</p>
        </div>

        {/* Form Kartı */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {isSuccess ? (
            // Başarı Durumu
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('forgotPassword.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('forgotPassword.successMessageSimple')}
              </p>
              <Link to="/login" className="btn-primary w-full">
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            // Form Durumu
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('forgotPassword.title')}</h2>
              <p className="text-gray-600 text-sm mb-6">
                {t('forgotPassword.subtitle')}
              </p>

              {/* Hata Mesajı */}
              {forgotPasswordMutation.isError && (
                <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {forgotPasswordMutation.error instanceof Error
                      ? forgotPasswordMutation.error.message
                      : t('forgotPassword.errorDefault')}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* E-posta */}
                <div>
                  <label htmlFor="email" className="label">
                    {t('forgotPassword.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    placeholder="sirket@example.com"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>

                {/* Gönder Butonu */}
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={forgotPasswordMutation.isPending}
                >
                  {forgotPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('forgotPassword.submitting')}
                    </>
                  ) : (
                    t('forgotPassword.submit')
                  )}
                </button>

                {/* Giriş Sayfasına Dön */}
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 text-sm text-maritime-600 hover:text-maritime-700 mt-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
